import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { Input } from "@ember/component";
import { hash } from "@ember/helper";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import DButton from "discourse/components/d-button";
import DModal from "discourse/components/d-modal";
import { extractError } from "discourse/lib/ajax-error";
import { emailValid } from "discourse/lib/utilities";
import UserChooser from "discourse/select-kit/components/user-chooser";
import { not, or } from "discourse/truth-helpers";
import { i18n } from "discourse-i18n";

export default class CircleAddMembersController extends Component {
  @service currentUser;
  @service siteSettings;
  @service router;

  @tracked loading = false;
  @tracked setOwner = false;
  @tracked notifyUsers = true;
  @tracked usernamesAndEmails = [];
  @tracked flash;

  get circle() {
    return this.args.model.circle;
  }

  get mode() {
    return this.args.model.mode;
  }

  get group() {
    return this.args.model.group;
  }

  get groupsAllowed() {
    return this.siteSettings.discourse_circles_groups_allowed_to_use_circles?.split(
      "|"
    );
  }

  get rawTitle() {
    return i18n(`discourse_circles.circle.add_members.title.${this.mode}`, {
      circle_name: this.args.model.circle.name,
    });
  }

  @computed("mode")
  get description() {
    return i18n(
      `discourse_circles.circle.add_members.description.${this.mode}`,
      {
        circle_name: this.args.model.circle.name,
      }
    );
  }

  get usernames() {
    return this.usernamesAndEmails.filter((x) => !emailValid(x)).join(",");
  }

  @action
  setUsernamesAndEmails(usernamesAndEmails) {
    this.usernamesAndEmails = usernamesAndEmails;

    if (this.emails) {
      if (!this.usernames) {
        this.notifyUsers = false;
      }

      this.setOwner = false;
    }
  }

  @action
  async addMembers() {
    if (isEmpty(this.usernamesAndEmails)) {
      return;
    }

    this.loading = true;

    try {
      await this.circle.addMembers(
        this.usernames,
        this.mode === "staff",
        this.notifyUsers
      );

      this.args.model.reloadMembers?.();

      this.router.transitionTo(
        `circle.${this.mode}`,
        this.circle.slug_path_with_id,
        {
          queryParams: {},
        }
      );

      this.args.closeModal();
    } catch (error) {
      this.flash = extractError(error);
    } finally {
      this.loading = false;
    }
  }

  <template>
    <DModal
      class="circles-add-members-modal"
      @title={{this.rawTitle}}
      @closeModal={{@closeModal}}
      @flash={{this.flash}}
      @flashType="error"
    >
      <:body>

        <form class="form-vertical circles-add-members">
          <p>{{this.description}}</p>

          <div class="input-group">
            <UserChooser
              @value={{this.usernamesAndEmails}}
              @onChange={{this.setUsernamesAndEmails}}
              @options={{hash
                allowEmails=false
                filterPlaceholder="discourse_circles.circle.add_members.usernames_placeholder"
                customSearchOptions=(hash groupIds=this.groupsAllowed)
              }}
            />
            <div class="control-instructions">
              {{i18n
                "discourse_circles.circle.add_members.only_allowed_disclaimer"
              }}
            </div>
          </div>

          {{#if this.currentUser.staff}}
            <div class="input-group circles-notify-users">
              <label>
                <Input @type="checkbox" @checked={{this.notifyUsers}} />
                {{i18n "discourse_circles.circle.add_members.notify_users"}}
              </label>
            </div>
          {{/if}}
        </form>
      </:body>

      <:footer>
        <DButton
          class="circles-add-members-button btn-primary"
          @action={{this.addMembers}}
          @icon="plus"
          @disabled={{or this.loading (not this.usernamesAndEmails)}}
          @label="groups.add"
        />
      </:footer>
    </DModal>
  </template>
}
